import React, { useEffect, useRef, useState } from 'react';

import { SvgIcon } from '@common/sprites';
import { Title } from '@barges/react';
import { CountdownCloseButton } from '@common/countdown_close_button/countdown_close_button';
import { useTranslation } from 'react-i18next';
import PurchaseButton from '@app/components/advisor_bonus/purchase_button';
import classNames from 'classnames';
import Const from '@app/config/const';
import { motion, AnimatePresence } from 'motion/react';
import classes from './classes.module.scss';

type State = 'waiting' | 'processing' | 'done';

type Props = {
  show: boolean,
  closeAfter: number,
  duration: number,
  amount: string,
  state: State,
  chatState: string,
  onClose: () => void,
  onButtonClicked: () => void
  onPurchaseCompleted: () => void;
  onPurchaseFailed: () => void;
  hideTopUpBanner: () => void;
};

export const QuickTopupBanner: React.FC<Props> = ({
  show, closeAfter, duration, amount, state, onClose, onButtonClicked, onPurchaseCompleted, onPurchaseFailed, chatState
}) => {
  const timer = useRef<NodeJS.Timeout>(null);
  const [timeLeft, setTimeLeft] = useState(closeAfter);
  const { t } = useTranslation(null, { keyPrefix: 'chat.topup_banner' });

  const renderButtonContent = () => {
    switch (state) {
      case 'processing':
        return (
          <Title fontStyle="content-medium" bold>
            {t('processing_btn_title')}
            {' '}
            <span className={ classes.dots } />
          </Title>
        );
      case 'done':
        return (
          <>
            <SvgIcon id="purchases_success" size={ 20 } />
            <Title fontStyle="content-medium" bold>{t('done_btn_title')}</Title>
          </>
        );
      default: return (
        <>
          <Title fontStyle="content-medium" bold>{t('add_btn_title', { count: duration })}</Title>
          <Title fontStyle="caption-2">{t('add_btn_subtitle', { amount })}</Title>
        </>
      );
    }
  };

  useEffect(() => {
    if (!show) return () => {};

    setTimeLeft(closeAfter);

    timer.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev - 1 >= 0) return prev - 1;
        clearInterval(timer.current);
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer.current);
  }, [show]);

  return (
    <AnimatePresence initial={ false }>
      {show && (
        <motion.div
          initial={ { height: 0, opacity: 0 } }
          animate={ { height: 'auto', opacity: 1 } }
          exit={ { height: 0, opacity: 0 } }
          transition={ { duration: 0.4, ease: 'easeInOut' } }
        >
          <div className={ classes.container }>
            <div className={ classes.titleAndIconContainer }>
              <div className={ classes.iconContainer }>
                <SvgIcon id="topup_banner_icon" className={ classes.icon } />
              </div>
              <div className={ classes.titleContainer }>
                <Title fontStyle="content-medium" bold>{t('title')}</Title>
                <div className={ classes.subtitle }>
                  <Title fontStyle="caption-1">{t('subtitle')}</Title>
                </div>
              </div>
            </div>
            <div className={ classes.btnWrapper }>
              <PurchaseButton
                key="quick-top-up-btn"
                isContinue
                purchaseAmount={ undefined }
                onPurchaseComplete={ onPurchaseCompleted }
                onPurchaseFailed={ onPurchaseFailed }
                renderStatusPopup={ chatState !== Const.chatStates.started }
                purchaseButtonTitle={ renderButtonContent() }
                disabled={ state !== 'waiting' }
                onClickHandler={ onButtonClicked }
                purchaseButtonTitleClassName={ classNames(classes.btn, {
                  [classes.add]: state === 'waiting',
                  [classes.processing]: state === 'processing',
                  [classes.done]: state === 'done'
                }) }
              />
            </div>
            {!!timeLeft && state === 'waiting' && (
              <div className={ classes.closeIconContainer }>
                <CountdownCloseButton totalDuration={ closeAfter } timeLeft={ timeLeft } onClose={ onClose } />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
